/**
 * Global Cmd+K search across every workspace object type (ADR-001 D6).
 *
 * One query per type, generated from the registry (src/lib/workspace-objects.ts)
 * and run in parallel. Each is org-scoped: RLS plus an explicit
 * organization_id filter, so a support or agency operate-as session searches
 * the operated client's data through the x-support-session-id header. A type
 * whose query fails is reported in `failed`, and the palette says so, instead
 * of quietly showing no rows for it.
 */
import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";

import { noteBodyToPlain } from "@/lib/mentions";
import { supabase } from "@/lib/supabase";
import {
  WORKSPACE_OBJECTS,
  WORKSPACE_OBJECT_TYPES,
  type WorkspaceObjectRow,
  type WorkspaceObjectType,
} from "@/lib/workspace-objects";

export type SearchEntityType = WorkspaceObjectType;

export interface SearchResult {
  type: SearchEntityType;
  id: string;
  label: string;
  sublabel?: string;
  /** SPA route to navigate to on select. */
  to: string;
}

export interface GlobalSearchResults {
  /** In registry order: contacts, companies, deals, projects, documents, notes, tasks. */
  results: SearchResult[];
  /** Types whose query failed; their rows are missing from `results`. */
  failed: SearchEntityType[];
}

export const ENTITY_LABELS = Object.fromEntries(
  WORKSPACE_OBJECT_TYPES.map((type) => [type, WORKSPACE_OBJECTS[type].pluralLabel]),
) as Record<SearchEntityType, string>;

export const PER_ENTITY_LIMIT = 6;
/**
 * public.projects has no Relay marker, so project search reads a bounded batch
 * of name matches and keeps those with a relay.project_extensions row. A match
 * beyond the batch can be missed; Phase 2's search index removes the limit.
 */
export const PROJECT_CANDIDATE_LIMIT = 50;

/**
 * Notes store mentions as @[Label](relay://…) (labels escaped) while people
 * search what they read (@Label). A note without mentions reads as stored, so
 * it must contain the term; one with mentions can read differently, so it is
 * always a candidate. The bounded batch is then matched on the readable text.
 */
export const NOTE_CANDIDATE_LIMIT = 50;

/**
 * The term as searched: whitespace collapsed and "*" dropped (PostgREST reads it
 * as a wildcard and it can't be escaped). Everything else, commas, parentheses,
 * quotes, % and _ included, is matched literally.
 */
export function searchTerm(term: string): string {
  return term.replace(/\*/g, " ").replace(/\s+/g, " ").trim();
}

/** Whether a term is long enough to search; recents show otherwise. */
export function isSearchable(term: string): boolean {
  return searchTerm(term).length >= 2;
}

/** A LIKE pattern containing `term` literally: \, % and _ escaped. */
function containsPattern(term: string): string {
  return `%${term.replace(/[\\%_]/g, "\\$&")}%`;
}

/** A PostgREST or() operand, double-quoted so , ( ) and quotes in it stay data. */
function quoted(value: string): string {
  return `"${value.replace(/[\\"]/g, "\\$&")}"`;
}

function toResult(type: SearchEntityType, row: WorkspaceObjectRow): SearchResult {
  const def = WORKSPACE_OBJECTS[type];
  const sublabel = def.search.sublabelOf?.(row);
  return { type, id: String(row.id), label: def.labelOf(row), ...(sublabel ? { sublabel } : {}), to: def.routeOf(row) };
}

function relayFrom(table: string) {
  return (supabase as unknown as SupabaseClient).schema("relay").from(table);
}

async function searchRelayType(type: Exclude<SearchEntityType, "project" | "note">, term: string, orgId: string) {
  const def = WORKSPACE_OBJECTS[type];
  const like = quoted(containsPattern(term));
  let query = relayFrom(def.table).select(def.select).eq("organization_id", orgId);
  if (def.softDeleteColumn) query = query.is(def.softDeleteColumn, null);
  if (def.activityType) query = query.eq("activity_type", def.activityType);
  for (const column of def.search.isNull ?? []) query = query.is(column, null);
  const { data, error } = await query
    .or(def.search.columns.map((column) => `${column}.ilike.${like}`).join(","))
    .order(def.search.order.column, { ascending: def.search.order.ascending, nullsFirst: def.search.order.nullsFirst })
    .limit(PER_ENTITY_LIMIT);
  if (error) throw error;
  return ((data ?? []) as unknown as WorkspaceObjectRow[]).map((row) => toResult(type, row));
}

// Projects are Forge's public.projects, read through a literal call site so the
// DB-coupling ratchet sees it; cross-schema embeds are avoided here on purpose.
async function searchNotes(term: string, orgId: string) {
  const def = WORKSPACE_OBJECTS.note;
  const { data, error } = await relayFrom(def.table)
    .select(def.select)
    .eq("organization_id", orgId)
    .eq("activity_type", "note")
    // A superset of readable matches: the term as stored, or any note with a mention.
    .or(`body.ilike.${quoted(containsPattern(term))},body.ilike.${quoted(containsPattern("](relay://"))}`)
    .order(def.search.order.column, { ascending: def.search.order.ascending })
    .limit(NOTE_CANDIDATE_LIMIT);
  if (error) throw error;
  const needle = term.toLowerCase();
  return ((data ?? []) as unknown as WorkspaceObjectRow[])
    .filter((row) => noteBodyToPlain(typeof row.body === "string" ? row.body : "").toLowerCase().includes(needle))
    .slice(0, PER_ENTITY_LIMIT)
    .map((row) => toResult("note", row));
}

async function searchProjects(term: string, orgId: string) {
  const { data, error } = await supabase
    .schema("public")
    .from("projects")
    .select("id, name")
    .eq("organization_id", orgId)
    .is("deleted_at", null)
    .ilike("name", containsPattern(term))
    .order("created_at", { ascending: false })
    .limit(PROJECT_CANDIDATE_LIMIT);
  if (error) throw error;
  const projects = data ?? [];
  if (projects.length === 0) return [];

  const { data: extensionRows, error: extensionError } = await supabase
    .schema("relay")
    .from("project_extensions")
    .select("project_id")
    .in("project_id", projects.map((p) => p.id));
  if (extensionError) throw extensionError;

  const relayProjectIds = new Set((extensionRows ?? []).map((row) => row.project_id));
  return projects
    .filter((p) => relayProjectIds.has(p.id))
    .slice(0, PER_ENTITY_LIMIT)
    .map((p) => toResult("project", p));
}

export function useGlobalSearch(term: string, organizationId: string | null, enabled: boolean) {
  const safe = searchTerm(term);
  return useQuery({
    queryKey: ["global-search", organizationId, safe],
    enabled: enabled && isSearchable(safe) && !!organizationId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<GlobalSearchResults> => {
      const orgId = organizationId!;
      const outcomes = await Promise.all(
        WORKSPACE_OBJECT_TYPES.map((type) =>
          (type === "project" ? searchProjects(safe, orgId)
            : type === "note" ? searchNotes(safe, orgId)
              : searchRelayType(type, safe, orgId)).then(
            (results) => ({ type, results, failed: false }),
            () => ({ type, results: [] as SearchResult[], failed: true }),
          ),
        ),
      );
      return {
        results: outcomes.flatMap((outcome) => outcome.results),
        failed: outcomes.filter((outcome) => outcome.failed).map((outcome) => outcome.type),
      };
    },
  });
}
